<template>
  <v-row>
    <v-col cols="12" lg="12">
      <Aggregate
        title="Generators"
        v-model="computedInput.generators"
        :error-messages="$utils.getIfExists(errors, ['input', 'generators'])"
        :ItemConstructor="generatorFactory"
        :headers="headers"
        copy-btn
        dense
        v-bind="$attrs"
        @change="$emit('change')"
      >
        <template #add="{ addItem, addItemMultiple, replaceItemMultiple }">
          <v-menu>
            <template #activator="{ on }">
              <v-btn icon v-on="on">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="addItem">
                <v-list-item-icon><v-icon>mdi-plus</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Create generator</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <DialogForm
                title="Import generators from file"
                maxWidth="600"
                accept=".xlsx"
                :submit="submitMultipleItems(addItemMultiple, replaceItemMultiple, generatorsForm)"
              >
                <template #activator="{ on: dialogOn }">
                  <v-list-item v-on="dialogOn">
                    <v-list-item-icon>
                      <v-icon>mdi-file-upload-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Upload from file</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <ImportSpreadsheet
                  v-model="generatorsForm"
                  :start-year="startYear"
                  :num-years="num_years"
                  :replace.sync="replaceImportedItems"
                ></ImportSpreadsheet>
              </DialogForm>
            </v-list>
          </v-menu>
        </template>

        <template #append-top>
          <v-dialog v-model="multipleEditDialog" max-width="600">
            <template #activator="{ on: dialogOn }">
              <v-tooltip bottom>
                <template #activator="{ on: tooltipOn }">
                  <v-btn icon v-on="{ ...tooltipOn, ...dialogOn }"
                    ><v-icon>mdi-pencil</v-icon></v-btn
                  >
                </template>
                <span>Edit multiple items</span>
              </v-tooltip>
            </template>
            <MultipleEdit
              :input.sync="input"
              :settings="settings"
              @close="multipleEditDialog = false"
              @change="$emit('change')"
            ></MultipleEdit>
          </v-dialog>
        </template>
        <template #item.active="{ item }">
          <v-switch
            v-model="item.active"
            dense
            hide-details
            class="mt-0"
            @change="$emit('change')"
          ></v-switch>
        </template>
        <template #item.type="{ item, errors, allowEdit }">
          <v-select
            v-model="item.type"
            :items="types"
            :error-messages="$utils.getIfExists(errors, ['type', 'messages'])"
            :disabled="!allowEdit || item.noDelete"
            label="Generator Type"
            single-line
            dense
            @change="$emit('change')"
          ></v-select>
        </template>
        <template #item.source="{ item, errors, allowEdit }">
          <span v-if="item.type === 'HYDRO'">Hydro</span>
          <v-select
            v-else
            v-model="item.source"
            :items="sources[item.type]"
            :error-messages="$utils.getIfExists(errors, ['source', 'messages'])"
            :disabled="!allowEdit || item.noDelete"
            label="Source"
            single-line
            dense
            @change="sourceChange($event, item.id)"
          ></v-select>
        </template>
        <template #item.bus_id="{ item }">
          <v-select
            clearable
            v-model="item.bus_id"
            :items="nodeIndex"
            :label="thermalSchema.bus_id.title"
            single-line
            dense
            @change="$emit('change')"
          >
            <template #selection="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
            <template #item="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
          </v-select>
        </template>
        <template #item.capacity="{ item }">
          <p class="mb-2">{{ item.capacity[0] }} MW</p>
        </template>
        <template #default="{ item, errors }">
          <v-card class="pa-2 ma-2" flat>
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">General</p>
                <InputGeo
                  v-model="item.coordinates"
                  label="Coordinates"
                  map-height="150"
                  nullable
                  switch-text="Set coordinates"
                  :icon="generatorIconName(item)"
                  :initial-coordinates="nodeCoordinates(item.bus_id)"
                  @change="$emit('change')"
                ></InputGeo>
                <InputTextField
                  v-model.number="item.technical_life"
                  :label="hydroSchema.technical_life.title"
                  :hint="`Technical life influences generation unit retirement, directly impacting the minimum installed capacity requirement. Retirement: ${retirementYear(
                    item
                  )}.`"
                  persistent-hint
                  :error-messages="$utils.getIfExists(errors, ['technical_life', 'messages'])"
                  suffix="years"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
                <InputToggleParam
                  v-model="item.operating_status"
                  :label="hydroSchema.operating_status.properties.active.title"
                  :hint="hydroSchema.operating_status.properties.active.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['operating_status'])"
                  @change="$emit('change')"
                >
                  <template v-slot="{ param }">
                    <InputProfile
                      v-model="param.id"
                      hint="Sets generator run mode (e.g., must-run, seasonal)"
                      profile-type="DAILY_OPERATING_STATUS"
                      hide-divider
                      @change="$emit('change')"
                    ></InputProfile>
                  </template>
                  <!-- <template #below="{param, disabled}"></template> -->
                </InputToggleParam>
                <InputTextField
                  v-model.number="item.start_year"
                  :label="hydroSchema.start_year.title"
                  :suffix="hydroSchema.start_year.unit"
                  :hint="hydroSchema.start_year.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['start_year', 'messages'])"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>

                <v-switch
                  v-model="item.include_elcc"
                  :label="hydroSchema.include_elcc.title"
                  :hint="hydroSchema.include_elcc.tooltip"
                  persistent-hint
                  dense
                  @change="$emit('change')"
                ></v-switch>

                <template v-if="item.type == 'THERMAL'">
                  <InputToggleParam
                    v-model="item.base_profile"
                    :label="thermalSchema.base_profile.properties.active.title"
                    :hint="thermalSchema.base_profile.properties.active.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['base_profile'])"
                    @change="$emit('change')"
                  >
                    <template #below="{ param, errors }">
                      <v-col class="grow">
                        <InputYearlyProfile
                          v-model="param.profiles"
                          :hint="
                            thermalSchema.base_profile.then.properties.param.properties.profiles
                              .tooltip
                          "
                          :suffix="
                            thermalSchema.base_profile.then.properties.param.properties.profiles
                              .unit
                          "
                          :error-messages="$utils.getIfExists(errors, ['profiles'])"
                          :num_years="num_years"
                          fill
                          :profile-type="item.type"
                          @change="$emit('change')"
                        ></InputYearlyProfile>
                      </v-col>
                    </template>
                  </InputToggleParam>

                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-switch
                        v-bind="$attrs"
                        v-model="item.minimum_operating_load.active"
                        :label="thermalSchema.minimum_operating_load.title"
                        :hint="thermalSchema.minimum_operating_load.tooltip"
                        persistent-hint
                        @change="$emit('change')"
                      ></v-switch>
                    </v-col>
                    <v-expand-transition>
                      <v-col v-if="item.minimum_operating_load.active" cols="12" sm="8">
                        <InputYearly
                          v-model="item.minimum_operating_load.value"
                          :hint="thermalSchema.minimum_operating_load.description"
                          :suffix="thermalSchema.minimum_operating_load.unit"
                          :error-messages="$utils.getIfExists(errors, ['value'])"
                          :num_years="num_years"
                          fill
                          hide-divider
                          :fill-value="1"
                          @change="$emit('change')"
                        ></InputYearly>
                      </v-col>
                    </v-expand-transition>
                  </v-row>

                  <InputTextField
                    v-if="item.source !== 'GEOTHERMAL'"
                    v-model.number="item.heat_rate"
                    :label="thermalSchema.heat_rate.title"
                    hint="Higher heat rate increases fuel consumption per MWh, raising unit operating costs"
                    :error-messages="$utils.getIfExists(errors, ['heat_rate', 'messages'])"
                    :suffix="thermalSchema.heat_rate.unit"
                    type="number"
                    @change="$emit('change')"
                  ></InputTextField>
                  <InputTextField
                    v-model.number="item.ramp_rate"
                    :label="thermalSchema.ramp_rate.title"
                    hint="This affects how quickly the generator unit can produce energy over consecutive operating hours"
                    :error-messages="$utils.getIfExists(errors, ['ramp_rate', 'messages'])"
                    suffix="MW/h"
                    type="number"
                    @change="$emit('change')"
                  ></InputTextField>
                  <InputTextField
                    v-if="item.source !== 'GEOTHERMAL'"
                    v-model.number="item.co2_rate"
                    :label="thermalSchema.co2_rate.title"
                    hint="This parameter indicates the CO₂ emissions produced per megawatt-hour (MWh) of electricity generated"
                    :error-messages="$utils.getIfExists(errors, ['co2_rate', 'messages'])"
                    :suffix="thermalSchema.co2_rate.unit"
                    type="number"
                    @change="$emit('change')"
                  ></InputTextField>
                  <InputTextField
                    v-model.number="item.availability_rate"
                    :label="thermalSchema.availability_rate.title"
                    :hint="thermalSchema.availability_rate.tooltip"
                    :suffix="thermalSchema.availability_rate.unit"
                    type="number"
                    :min="thermalSchema.availability_rate.minimum"
                    :max="thermalSchema.availability_rate.maximum"
                    @change="$emit('change')"
                  ></InputTextField>
                </template>

                <template v-if="item.type == 'RENEWABLE'">
                  <!-- <v-select v-model="item.source" label="Source" :items="sources.RENEWABLE"></v-select> -->
                  <InputYearlyProfile
                    v-model="item.profile"
                    :title="renewableSchema.profile.title"
                    :suffix="renewableSchema.profile.unit"
                    hint="This parameter defines the hourly generation profile expressed in per unit (p.u.) values"
                    :error-messages="$utils.getIfExists(errors, ['profile'])"
                    :num_years="num_years"
                    :year-start="startYear"
                    :profile-type="item.source"
                    :fill="['HYDRO_ROR', 'SOLAR_PV', 'WIND'].includes(item.source)"
                    @change="$emit('change')"
                  ></InputYearlyProfile>
                  <InputToggleParam
                    v-model="item.joint_ramping"
                    :error-messages="$utils.getIfExists(errors, ['joint_ramping'])"
                    label="Renewable + Storage Ramping Constraint"
                    @change="$emit('change')"
                  >
                    <template v-slot="{ param, errors }">
                      <v-text-field
                        v-model.number="param.ramp_rate"
                        :error-messages="$utils.getIfExists(errors, ['ramp_rate', 'messages'])"
                        label="Ramp Rate"
                        suffix="%/h"
                        type="number"
                        @change="$emit('change')"
                      ></v-text-field>
                    </template>
                    <template #below="{ param, disabled, errors }">
                      <v-autocomplete
                        v-model="param.storages"
                        :error-messages="$utils.getIfExists(errors, ['storages', 'messages'])"
                        label="Energy Storage Units"
                        :items="input.storage"
                        :disabled="disabled"
                        chips
                        multiple
                        deletable-chips
                        item-text="name"
                        item-value="id"
                        @change="$emit('change')"
                      ></v-autocomplete>
                    </template>
                  </InputToggleParam>
                </template>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Expansion</p>
                <v-switch
                  v-model="item.expansion_candidate"
                  label="Expansion Candidate"
                  :error-messages="$utils.getIfExists(errors, ['expansion_candidate'])"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-switch
                  v-if="item.type === 'THERMAL'"
                  v-model="item.count_as_renewable"
                  label="Count as Renewable"
                  hint="For minimum renewable participation (see Policies)"
                  persistent-hint
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearly
                  v-model="item.investment"
                  :label="hydroSchema.investment.title"
                  hint="This parameter indicates the investment cost for constructing the unit and applies exclusively to the AA module"
                  :error-messages="$utils.getIfExists(errors, ['investment'])"
                  suffix="$/KW"
                  :num_years="num_years"
                  :year_start="startYear"
                  type="number"
                  @change="$emit('change')"
                  fill
                  :fill-value="item.type == 'THERMAL' ? 8000 : hydroSchema.investment.items.default"
                ></InputYearly>
                <InputYearly
                  v-model="item.maximum_build"
                  :label="hydroSchema.maximum_build.title"
                  hint="This parameter defines the maximum build quantity for the unit and applies exclusively to the AA module"
                  :error-messages="$utils.getIfExists(errors, ['maximum_build'])"
                  suffix="MW"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  @change="$emit('change')"
                  fill
                  :fill-value="hydroSchema.maximum_build.items.default"
                ></InputYearly>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Metrics</p>
                <v-switch
                  v-model="item.LCOE_Calculation"
                  :label="hydroSchema.LCOE_calculation.title"
                  :hint="hydroSchema.LCOE_calculation.tooltip"
                  persistent-hint
                  :error-messages="$utils.getIfExists(errors, ['LCOE_Calculation'])"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Financial</p>
                <InputTextField
                  v-model.number="item.economic_life"
                  :label="hydroSchema.economic_life.title"
                  :hint="hydroSchema.economic_life.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['economic_life', 'messages'])"
                  suffix="years"
                  type="number"
                  :class="`my-${inputMargin}`"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.discount"
                  :label="hydroSchema.discount.title"
                  :suffix="hydroSchema.discount.unit"
                  :hint="hydroSchema.discount.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['discount', 'messages'])"
                  :placeholder="
                    input.discount && input.discount != null ? input.discount.toString() : ''
                  "
                  type="number"
                  :class="`my-${inputMargin}`"
                  @change="$emit('change')"
                ></InputTextField>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Costs</p>
                <InputYearly
                  v-model="item.vom"
                  :label="hydroSchema.vom.title"
                  hint="This parameter defines the variable costs excluding fuel cost for the unit"
                  :error-messages="$utils.getIfExists(errors, ['vom'])"
                  :num_years="num_years"
                  :year_start="startYear"
                  suffix="$/MWh"
                  type="number"
                  @change="$emit('change')"
                  fill
                  :fill-value="hydroSchema.vom.items.default"
                ></InputYearly>
                <InputTextField
                  v-model.number="item.fom"
                  :label="hydroSchema.fom.title"
                  hint="This parameter specifies the fixed cost for the unit and applies exclusively to the AA module"
                  :error-messages="$utils.getIfExists(errors, ['fom', 'messages'])"
                  suffix="$/KW-years"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
                <InputYearly
                  v-if="item.type === 'RENEWABLE'"
                  v-model="item.curtailment_penalty_price"
                  :label="renewableSchema.curtailment_penalty_price.title"
                  hint="Cost assigned to unused available energy."
                  :suffix="renewableSchema.curtailment_penalty_price.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="renewableSchema.curtailment_penalty_price.items.default"
                  @change="$emit('change')"
                ></InputYearly>
                <template v-if="item.type == 'THERMAL' && item.source !== 'GEOTHERMAL'">
                  <v-switch
                    v-model="item.profile_fuel_price"
                    :label="thermalSchema.profile_fuel_price.title"
                    hint="This parameter defines the fuel price for the unit and may be specified as either an annual constant or an hourly price profile"
                    persistent-hint
                    dense
                    @change="$emit('change')"
                  ></v-switch>
                  <InputYearlyProfile
                    v-if="item.profile_fuel_price"
                    v-model="item.fuel_price_profiles"
                    :label="thermalSchema.fuel_price_profiles.title"
                    :hint="thermalSchema.fuel_price_profiles.tooltip"
                    :num_years="num_years"
                    :profile-type="thermalSchema.fuel_price_profiles.profileType"
                    @change="$emit('change')"
                  ></InputYearlyProfile>
                  <InputYearly
                    v-else
                    v-model="item.fuel_price"
                    :label="thermalSchema.fuel_price.title"
                    :hint="thermalSchema.fuel_price.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['fuel_price'])"
                    :suffix="thermalSchema.fuel_unit.default"
                    :num_years="num_years"
                    :year_start="startYear"
                    type="number"
                    fill
                    :fill-value="thermalSchema.fuel_price.items.default"
                    @change="$emit('change')"
                  >
                    <template #default="{ year, value, type, readonly, errorMessages }">
                      <v-text-field
                        v-model.number="value[year - 1]"
                        :error-messages="errorMessages"
                        :label="`Year ${year}`"
                        :type="type"
                        :suffix="thermalSchema.fuel_price.unit"
                        :readonly="readonly"
                        @change="$emit('change')"
                      >
                      </v-text-field>
                    </template>
                  </InputYearly>
                </template>

                <!-- PPAs -->
                <v-switch
                  v-model="item.ppa.active"
                  :label="thermalSchema.ppa.title"
                  :hint="thermalSchema.ppa.tooltip"
                  persistent-hint
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.energy"
                  object-value
                  fill
                  toggle-items-type
                  :label="thermalSchema.ppa.properties.energy.title"
                  :hint="thermalSchema.ppa.properties.energy.tooltip"
                  suffix="$/MWh"
                  :num_years="num_years"
                  :profile-type="thermalSchema.ppa.properties.energy.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.deemedEnergy"
                  object-value
                  fill
                  toggle-items-type
                  :label="thermalSchema.ppa.properties.deemedEnergy.title"
                  :hint="thermalSchema.ppa.properties.deemedEnergy.tooltip"
                  suffix="$/MWh"
                  :num_years="num_years"
                  :profile-type="thermalSchema.ppa.properties.deemedEnergy.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.capacity"
                  object-value
                  fill
                  toggle-items-type
                  :label="thermalSchema.ppa.properties.capacity.title"
                  :hint="thermalSchema.ppa.properties.capacity.tooltip"
                  suffix="$/MW-month"
                  :num_years="num_years"
                  :profile-type="thermalSchema.ppa.properties.capacity.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>

                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Capacity</p>
                <template v-if="item.type == 'THERMAL'">
                  <InputYearly
                    v-model="item.capacity"
                    :label="thermalSchema.capacity.title"
                    hint="This parameter defines the minimum installed generator capacity for the AA module. Any increase must align with the maximum build limit. For the NPC module, this input considers only capacity."
                    :error-messages="$utils.getIfExists(errors, ['capacity'])"
                    suffix="MW"
                    :num_years="num_years"
                    :year_start="startYear"
                    type="number"
                    :available-years="maxCapacityYears(item)"
                    fill
                    :fill-value="thermalSchema.capacity.items.default"
                    @change="$emit('change')"
                  ></InputYearly>
                  <InputTextField
                    v-model.number="item.derate_summer"
                    :label="thermalSchema.derate_summer.title"
                    :hint="thermalSchema.derate_summer.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['derate_summer', 'messages'])"
                    suffix="%"
                    type="number"
                    @change="$emit('change')"
                  ></InputTextField>
                  <InputTextField
                    v-model.number="item.derate_winter"
                    :label="thermalSchema.derate_winter.title"
                    :hint="thermalSchema.derate_winter.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['derate_winter', 'messages'])"
                    suffix="%"
                    type="number"
                    @change="$emit('change')"
                  ></InputTextField>
                </template>
                <template v-if="item.type == 'RENEWABLE'">
                  <InputYearly
                    v-model="item.capacity"
                    label="Minimum Installed Capacity"
                    hint="This parameter defines the minimum installed generator capacity for the AA module. Any increase must align with the maximum build limit. For the NPC module, this input considers only capacity."
                    :error-messages="$utils.getIfExists(errors, ['capacity'])"
                    suffix="MW"
                    :num_years="num_years"
                    :year_start="startYear"
                    :available-years="maxCapacityYears(item)"
                    type="number"
                    @change="$emit('change')"
                    fill
                    :fill-value="renewableSchema.capacity.items.default"
                  ></InputYearly>
                  <InputTextField
                    v-model.number="item.firm"
                    label="Firm Power"
                    :hint="renewableSchema.firm.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['firm', 'messages'])"
                    suffix="%"
                    type="number"
                    @change="$emit('change')"
                  ></InputTextField>
                </template>
                <template v-if="item.type == 'HYDRO'">
                  <InputYearly
                    v-model="item.capacity"
                    :label="hydroSchema.capacity.title"
                    hint="This parameter defines the minimum installed generator capacity for the AA module. Any increase must align with the maximum build limit. For the NPC module, this input considers only capacity."
                    :error-messages="$utils.getIfExists(errors, ['capacity'])"
                    suffix="MW"
                    :num_years="num_years"
                    :year_start="startYear"
                    :available-years="maxCapacityYears(item)"
                    type="number"
                    fill
                    :fill-value="hydroSchema.capacity.items.default"
                    @change="$emit('change')"
                  ></InputYearly>
                  <v-text-field
                    v-model.number="item.firm"
                    :label="hydroSchema.firm.title"
                    :hint="hydroSchema.firm.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['firm', 'messages'])"
                    suffix="%"
                    type="number"
                    :class="`my-${inputMargin}`"
                    @change="$emit('change')"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="item.ramp_rate"
                    :label="hydroSchema.ramp_rate.title"
                    :hint="hydroSchema.ramp_rate.tooltip"
                    :error-messages="$utils.getIfExists(errors, ['ramp_rate', 'messages'])"
                    suffix="MW/h"
                    type="number"
                    :class="`my-${inputMargin}`"
                    @change="$emit('change')"
                  ></v-text-field>
                </template>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6" v-if="['THERMAL', 'HYDRO'].includes(item.type)">
                <p class="text-subtitle-1 font-weight-bold">Reserves</p>
                <InputReserve
                  v-model="item.reserves"
                  :label="hydroSchema.reserves.title"
                  :hint="hydroSchema.reserves.tooltip"
                  :reserves="input.reserves"
                  :error-messages="$utils.getIfExists(errors, ['reserves'])"
                  suffix="%"
                  type="number"
                  @change="$emit('change')"
                ></InputReserve>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <template v-if="item.type == 'HYDRO'">
                <v-expansion-panels multiple>
                  <v-expansion-panel class="elevation-1">
                    <v-expansion-panel-header>
                      <div class="text-h6">
                        {{ hydroSchema.monthly_energy.title }}
                      </div>
                      <div class="text-caption">
                        {{ hydroSchema.monthly_energy.tooltip }}
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-3">
                      <InputYearly
                        v-for="month in months"
                        :key="month"
                        v-model="item.monthly_energy[month]"
                        :error-messages="$utils.getIfExists(errors, ['monthly_energy', month])"
                        :num_years="num_years"
                        :label="month.toUpperCase()"
                        suffix="GWh"
                        type="number"
                        @change="$emit('change')"
                        fill
                        :fill-value="hydroSchema.monthly_energy.properties[month].items.default"
                      ></InputYearly>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Power Flow Parameters</p>
                <template>
                  <v-select
                    v-model="item.power_flow_generator_type"
                    :items="powerFlowGenTypes(item)"
                    label="Type of the generator for power flow calculations"
                    @change="$emit('change')"
                  />
                </template>
                <template
                  v-if="
                    settings.POWER_FLOW.source === 'Case Inputs' &&
                    ['sync', 'static'].includes(item.power_flow_generator_type)
                  "
                >
                  <v-switch
                    v-model="item.power_profile"
                    :label="thermalSchema.power_profile.title"
                    :hint="thermalSchema.power_profile.tooltip"
                    persistent-hint
                    dense
                    @change="$emit('change')"
                  ></v-switch>
                  <InputYearlyProfile
                    v-if="item.power_profile"
                    v-model="item.active_power"
                    :label="thermalSchema.active_power_profile.title"
                    :suffix="thermalSchema.active_power_profile.unit"
                    :hint="thermalSchema.active_power_profile.tooltip"
                    :num_years="num_years"
                    fill
                    :fill-value="thermalSchema.active_power_profile.items.default.value"
                    object-value
                    items-type="profile"
                    @change="$emit('change')"
                  ></InputYearlyProfile>
                  <InputYearlyProfile
                    v-else
                    v-model="item.active_power"
                    :label="thermalSchema.active_power.title"
                    :suffix="thermalSchema.active_power.unit"
                    :hint="thermalSchema.active_power.tooltip"
                    :num_years="num_years"
                    fill
                    :fill-value="thermalSchema.active_power.items.default.value"
                    object-value
                    items-type="constant"
                    @change="$emit('change')"
                  ></InputYearlyProfile>
                  <InputTextField
                    v-model.number="item.scaling_factor"
                    :label="thermalSchema.scaling_factor.title"
                    :suffix="thermalSchema.scaling_factor.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </template>
                <template v-if="item.power_flow_generator_type === 'static'">
                  <InputTextField
                    v-model.number="item.reactive_power"
                    :label="renewableSchema.reactive_power.title"
                    :suffix="renewableSchema.reactive_power.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </template>
                <template v-if="item.power_flow_generator_type === 'sync'">
                  <v-switch
                    v-model="item.voltage_profile"
                    :label="thermalSchema.voltage_profile.title"
                    :hint="thermalSchema.voltage_profile.tooltip"
                    persistent-hint
                    dense
                    @change="$emit('change')"
                  ></v-switch>

                  <InputYearlyProfile
                    v-if="item.voltage_profile"
                    v-model="item.voltage_set_point"
                    :label="thermalSchema.voltage_set_point_profile.title"
                    :suffix="thermalSchema.voltage_set_point_profile.unit"
                    :hint="thermalSchema.voltage_set_point_profile.tooltip"
                    :num_years="num_years"
                    fill
                    :fill-value="thermalSchema.voltage_set_point_profile.items.default.value"
                    object-value
                    items-type="profile"
                    @change="$emit('change')"
                  ></InputYearlyProfile>
                  <InputYearlyProfile
                    v-else
                    v-model="item.voltage_set_point"
                    :label="thermalSchema.voltage_set_point.title"
                    :suffix="thermalSchema.voltage_set_point.unit"
                    :hint="thermalSchema.voltage_set_point.tooltip"
                    :num_years="num_years"
                    fill
                    :fill-value="thermalSchema.voltage_set_point.items.default.value"
                    object-value
                    items-type="constant"
                    @change="$emit('change')"
                  ></InputYearlyProfile>
                </template>
                <InputTextField
                  v-model.number="item.nominal_power"
                  :label="thermalSchema.nominal_power.title"
                  :suffix="thermalSchema.nominal_power.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <v-switch
                  v-if="item.power_flow_generator_type !== 'slack'"
                  v-model="item.run_contingency"
                  label="Run contingency"
                  hint="Consider this generator in an n-1 contingency analysis. Can't be done for slack generators."
                  persistent-hint
                ></v-switch>
                <v-divider class="mt-4"></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Short Circuit Parameters</p>
                <template v-if="defaultStatic(item.source)">
                  <InputTextField
                    v-model="item.nominal_current_to_SC_ratio"
                    :label="renewableSchema.nominal_current_to_SC_ratio.title"
                    :suffix="renewableSchema.nominal_current_to_SC_ratio.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </template>
                <template v-else>
                  <InputTextField
                    v-model.number="item.rated_voltage"
                    :label="thermalSchema.rated_voltage.title"
                    :suffix="thermalSchema.rated_voltage.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <InputTextField
                    v-model.number="item.subtransient_reactance"
                    :label="thermalSchema.subtransient_reactance.title"
                    :suffix="thermalSchema.subtransient_reactance.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <InputTextField
                    v-model.number="item.subtransient_resistance"
                    :label="thermalSchema.subtransient_resistance.title"
                    :suffix="thermalSchema.subtransient_resistance.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <InputTextField
                    v-model.number="item.cos_phi"
                    :label="thermalSchema.cos_phi.title"
                    :suffix="thermalSchema.cos_phi.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </template>
                <v-divider class="mt-4"></v-divider>
              </v-card>
            </v-card-text>
          </v-card>
        </template>
      </Aggregate>
    </v-col>
    <v-col cols="12" lg="12">
      <v-expansion-panels popout class="mb-3">
        <v-expansion-panel @input="chartVisible = $event" class="elevation-1">
          <template #header>
            <div class="text-h6">Generator Capacity by Fuel Type</div>
          </template>
          <ChartYearly
            :visible="chartVisible"
            type="doughnut"
            v-bind="generatorChart"
          ></ChartYearly>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
    <v-col cols="12" lg="12">
      <CardSimple title="Capacity of All Units">
        <ChartStacked
          :items="[...input.generators, ...input.storage]"
          valueKey="capacity"
          :num_years="num_years"
        ></ChartStacked>
      </CardSimple>
    </v-col>
  </v-row>
</template>

<script>
import chartUtils from "@/mixins/chartUtils";
import fieldUtils from "@/mixins/fieldUtils";
import { generatorIconName } from "@/plugins/map-utils";
import { CaseModel, schemas } from "data-dictionary";

export default {
  name: "SystemGenerator",
  mixins: [fieldUtils, chartUtils],
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    DialogForm: () => import("@/components/DialogForm.vue"),
    CardSimple: () => import("@/components/CardSimple.vue"),
    ImportSpreadsheet: () => import("@/components/forms/ImportSpreadsheet.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputReserve: () => import("@/components/InputReserve.vue"),
    InputProfile: () => import("@/components/InputProfile.vue"),
    ChartYearly: () => import("@/components/charts/ChartYearly.vue"),
    ChartStacked: () => import("@/components/charts/ChartStacked.vue"),
    InputToggleParam: () => import("@/components/InputToggleParam.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
    MultipleEdit: () => import("@/components/forms/MultipleEdit.vue"),
  },
  props: {
    num_years: {
      type: Number,
    },
    startYear: {
      type: Number,
      required: true,
    },
    input: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
    settings: { type: Object, required: true },
  },

  data() {
    return {
      inputMargin: 4,
      hydroSchema: schemas.find((v) => v.$id === "generator_hydro.schema.json").properties,
      renewableSchema: schemas.find((v) => v.$id === "generator_renewable.schema.json").properties,
      thermalSchema: {
        ...schemas.find((v) => v.$id === "generator_thermal.schema.json").properties,
        ...schemas
          .find((v) => v.$id === "generator_thermal.schema.json")
          .allOf.map((v) => v.then.properties)
          .reduce((obj, item) => ({ ...obj, ...item }), {}),
      },
      chartVisible: null,
      months: ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"],
      headers: [
        { text: "Active", value: "active", width: 30 },
        { text: "Name", value: "name" },
        { text: "Type", value: "type" },
        { text: "Source", value: "source" },
        { text: "Connected Node", value: "bus_id" },
        { text: "Capacity", value: "capacity" },
      ],
      types: CaseModel.Generator.TYPES,
      sources: CaseModel.Generator.SOURCES,
      generatorsForm: [],
      multipleEditDialog: false,
      replaceImportedItems: false,
    };
  },
  mounted() {},
  computed: {
    nodeIndex() {
      return this.computedInput.nodes
        ? this.computedInput.nodes.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
    generatorChart() {
      const generators = this.input.generators.map(this.mapToValue("capacity"));
      return this.pieObjectByField("pie", generators, "source");
    },
    generatorType() {
      return this.input.generators.map((v) => v.type);
    },
  },
  watch: {
    generatorType(val) {
      val.forEach((item, i) => {
        if (item === "HYDRO" && this.input.generators[i].source !== "HYDRO") {
          const inputCopy = this.input;
          inputCopy.generators[i].source = "HYDRO";
          this.$emit("update:input", inputCopy);
          this.$emit("change");
        }
      });
    },
  },
  methods: {
    generatorFactory: CaseModel.Generator,
    generatorIconName,
    sourceChange($event, id) {
      const item = this.input.generators.find((g) => g.id === id);
      if ($event === "GEOTHERMAL") {
        item.fuel_price = Array(this.num_years).fill(0);
        item.heat_rate = 0;
      }
      if (this.defaultStatic($event)) {
        item.power_flow_generator_type = "static";
      } else {
        item.power_flow_generator_type = "sync";
      }
      this.$emit("change");
    },
    powerFlowGenTypes(item) {
      const ret = [
        { text: "PV", value: "sync" },
        { text: "PQ", value: "static" },
        { text: "Slack", value: "slack" },
      ];
      if (this.defaultStatic(item.source)) {
        // solar and wind can't be slack
        ret.pop();
      }
      return ret;
    },
    defaultStatic(source) {
      return source === "SOLAR_PV" || source === "WIND";
    },
    maxCapacityYears(item) {
      const projectStartYear = this.startYear;
      const maxYears = this.retirementYear(item) - projectStartYear + 1;
      return maxYears >= 0 ? maxYears : 0;
    },
    retirementYear(item) {
      return item.start_year + item.technical_life - 2;
    },
    submitMultipleItems(addItemsFn, replaceItemsFn, generatorsForm) {
      if (this.replaceImportedItems) {
        return () => Promise.resolve(replaceItemsFn(generatorsForm));
      }
      return () => Promise.resolve(addItemsFn(generatorsForm));
    },
    nodeCoordinates(busId) {
      return busId ? this.input.nodes.find((v) => v.id === busId).coordinates : undefined;
    },
  },
};
</script>

<style>
.v-input__append-outer {
  margin: 0 0 0 0;
}
</style>
